import { TokenSelectControls } from '@/components/features/Account/AccountNFTList/TokenSelectControls';
import { useUserTokens } from '@/hooks/use-user-tokens';
import { useAuthentication } from '@/hooks/useAuthentication';
import { GetUsersUserTokensV7Params } from '@/lib/reservoir/reservoir.generated';
import { TOKEN_QUERY_LIMIT } from '@/utils/getTokenQueryFromParams';
import { Button, Container, Flex, Text } from '@chakra-ui/react';
import { compareAddresses } from '@onbeam/utils';
import { DynamicTokens } from '@sphere/reservoir-kit-ui';
import { FilterDrawer } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import NFTList, { Variants } from '../../NFT/NFTList';
import SortingOptions from '../../Search/Facets/SortingOptions';
import SearchActionBar from '../../Search/SearchActionBar';
import {
  USER_TOKENS_DEFAULT_SORT_OPTION,
  USER_TOKENS_SORT_OPTIONS,
  UserTokensSortOption,
} from '../../Search/config';

type Props = {
  address: string;
};

const AccountNFTList = ({ address }: Props) => {
  const router = useRouter();
  const { userAddress } = useAuthentication();
  const { t } = useTranslation('search');
  const searchParams = useSearchParams();
  const sortOption =
    USER_TOKENS_SORT_OPTIONS.find(o => o.label === searchParams.get('sortBy')) ||
    USER_TOKENS_DEFAULT_SORT_OPTION;

  const tokenQuery: GetUsersUserTokensV7Params = {
    ...sortOption.value,
    limit: TOKEN_QUERY_LIMIT,
  };

  const {
    data: tokens,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isInitialLoading,
    isFetching,
    refetch,
    isLoading,
  } = useUserTokens(address, tokenQuery);

  const onSortOptionChange = (option: UserTokensSortOption) => {
    router.replace({ query: { ...router.query, sortBy: option.label } }, undefined, {
      shallow: true,
    });
  };

  const isOwner = compareAddresses(userAddress, address);

  return (
    <>
      <SearchActionBar<GetUsersUserTokensV7Params>
        sortOptions={USER_TOKENS_SORT_OPTIONS}
        sortOption={sortOption}
        setSortOption={onSortOptionChange}
        isSortDisabled={!tokens.length && !isFetching}
        refetch={refetch}
        isLoading={isFetching}
      >
        {isOwner && <TokenSelectControls onRefresh={refetch} />}
      </SearchActionBar>
      <Container variant="breakout">
        <Flex flexDir="column" gap="space.16" display={{ base: 'flex', md: 'none' }} mb="space.24">
          <FilterDrawer title={t('common:drawers.sort-and-filter.sort-by.title')}>
            <SortingOptions<GetUsersUserTokensV7Params>
              sortOptions={USER_TOKENS_SORT_OPTIONS}
              isDisabled={!tokens.length && !isFetching}
              value={sortOption}
              onChange={onSortOptionChange}
              variant="list"
            />
          </FilterDrawer>
          {isOwner && <TokenSelectControls onRefresh={refetch} />}
        </Flex>
        {!tokens.length && !isInitialLoading && (
          <Text textAlign="center" fontSize="md" color="gray.5">
            {t('search:no-available-nfts')}
          </Text>
        )}
        <NFTList
          items={
            tokens.map(token => ({
              ...token,
              market: {
                floorAsk: token.ownership?.floorAsk,
                topBid: token.token?.topBid,
              },
            })) as DynamicTokens
          }
          isLoading={isLoading && !tokens.length}
          variant={Variants.Profile}
          isOwner={isOwner}
          mutate={refetch}
        />
        <Flex justifyContent="center" mt="space.24">
          {hasNextPage && (
            <Button
              mt="space.16"
              variant="secondary"
              onClick={() => fetchNextPage()}
              isLoading={isFetchingNextPage}
            >
              {t('load-more.button.label')}
            </Button>
          )}
        </Flex>
      </Container>
    </>
  );
};

export default AccountNFTList;
